import { FC, useMemo } from 'react';
import { ConfirmDialog, WbSelect } from '@agilelab/plugin-wb-platform';
import { useForm, Controller } from 'react-hook-form';
import { useDeleteTeamRoleAssignment } from '../../../hooks';
import {
  TeamRoleAssignment,
  TeamRoleAssignmentKindAsString,
  TeamRoleAssignmentKindEnum,
} from '../../../types';
import { useTeamRolesPanelContext } from '../../../context';
import { TeamRoleEnum } from '@agilelab/plugin-wb-team-roles-common';
import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import { useNavigate, useLocation } from 'react-router';
import { useAsync } from 'react-use';

type DeleteTeamRoleAssignmentDialogProps = {
  assignment: TeamRoleAssignment | null;
  onClose: () => void;
};

export const DeleteTeamRoleAssignmentDialog: FC<
  DeleteTeamRoleAssignmentDialogProps
> = ({ assignment, onClose }) => {
  const {
    projectUrn,
    selectedTeamRole,
    isOwner,
    isLimitedOwner,
    hasTroubleshootAccess,
    assignments,
    revokeAccess,
    enabledTeamRoles,
  } = useTeamRolesPanelContext();

  const identityApi = useApi(identityApiRef);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { value: currentUserEntityRef } = useAsync(
    () => identityApi.getBackstageIdentity().then(i => i.userEntityRef),
    [identityApi],
  );
  const { isLoading: deleting, mutate: deleteFn } =
    useDeleteTeamRoleAssignment();

  const isLastPrincipal = useMemo(() => {
    if (!assignment) {
      return false;
    }
    if (!assignment.kinds.includes(TeamRoleAssignmentKindEnum.FULL)) {
      return false;
    }

    const principalCount = (assignments || []).reduce(
      (count, ass) =>
        count + Number(ass.kinds.includes(TeamRoleAssignmentKindEnum.FULL)),
      0,
    );

    return principalCount <= 1;
  }, [assignments, assignment]);

  const deletableTypes = useMemo(() => {
    if (!assignment?.kinds) return [];

    const types = [];
    if (hasTroubleshootAccess) {
      types.push(
        ...assignment.kinds.filter(
          kind =>
            kind === TeamRoleAssignmentKindEnum.FULL ||
            kind === TeamRoleAssignmentKindEnum.LIMITED,
        ),
      );
    } else if (selectedTeamRole === TeamRoleEnum.OWNER && isOwner) {
      types.push(
        ...assignment.kinds.filter(
          kind => kind === TeamRoleAssignmentKindEnum.LIMITED,
        ),
      );

      if (!isLastPrincipal) {
        types.push(
          ...assignment.kinds.filter(
            kind => kind === TeamRoleAssignmentKindEnum.FULL,
          ),
        );
      }
    } else if (selectedTeamRole === TeamRoleEnum.OWNER && isLimitedOwner) {
      types.push(
        ...assignment.kinds.filter(
          kind => kind === TeamRoleAssignmentKindEnum.LIMITED,
        ),
      );
    } else {
      types.push(...assignment.kinds);
    }

    return types;
  }, [
    assignment?.kinds,
    hasTroubleshootAccess,
    selectedTeamRole,
    isOwner,
    isLimitedOwner,
    isLastPrincipal,
  ]);

  // Setup form per la selezione del tipo quando ci sono multiple opzioni
  const form = useForm<{ assignmentType: string }>({
    defaultValues: {
      assignmentType:
        deletableTypes.length > 1
          ? TeamRoleAssignmentKindEnum.LIMITED
          : deletableTypes[0] || '',
    },
  });

  const selectedAssignmentType = form.watch('assignmentType');

  const assignmentToDelete =
    deletableTypes.length === 1
      ? deletableTypes[0]
      : (selectedAssignmentType as TeamRoleAssignmentKindEnum) ||
        deletableTypes[0];

  const displayAssignmentType = deletableTypes[0] || assignmentToDelete;
  const assignmentTypeLabel = displayAssignmentType
    ? TeamRoleAssignmentKindAsString[displayAssignmentType]
    : 'assignment';

  const currentTeamRole = useMemo(
    () => enabledTeamRoles.find(teamRole => teamRole.name === selectedTeamRole),
    [enabledTeamRoles, selectedTeamRole],
  );

  const dialogDescription = useMemo(() => {
    if (deletableTypes.length === 0) return '';

    if (deletableTypes.length === 1) {
      return `The selected ${assignmentTypeLabel.toLowerCase()} for the ${
        currentTeamRole?.displayName
      } Team Role will be deleted. This action cannot be undone.`;
    }

    return (
      <div>
        <div>
          This assignment has multiple roles for the{' '}
          {currentTeamRole?.displayName} Team Role. Please select which role you
          want to delete. This action cannot be undone.
        </div>
        <Controller
          name="assignmentType"
          control={form.control}
          render={({ field, fieldState: { error } }) => (
            <WbSelect
              fullWidth
              id="assignment-type-to-delete"
              required
              label="Role to delete"
              placeholder="Choose the role to delete"
              options={deletableTypes}
              value={field.value ?? TeamRoleAssignmentKindEnum.LIMITED}
              onChange={value => {
                field.onChange(value || '');
              }}
              error={Boolean(error)}
              helperText={error?.message || 'Select the role to delete'}
              style={{ marginTop: 16 }}
            />
          )}
        />
      </div>
    );
  }, [deletableTypes, assignmentTypeLabel, currentTeamRole, form.control]);

  return (
    <ConfirmDialog
      inProgress={deleting}
      open={Boolean(assignment)}
      onClick={e => e.stopPropagation()}
      onClose={onClose}
      title={
        deletableTypes.length === 1
          ? `Delete ${assignmentTypeLabel.toLowerCase()} assignment?`
          : 'Delete assignment role?'
      }
      description={dialogDescription}
      confirmButtonText="Delete"
      onConfirm={() => {
        if (!assignment || !assignmentToDelete) return;

        deleteFn(
          {
            projectUrn,
            teamRole: selectedTeamRole,
            subject: assignment.subject?.urn,
            assignmentType: assignmentToDelete,
          },
          {
            onSuccess: () => {
              onClose();
              if (
                currentUserEntityRef &&
                assignment.subject?.urn === currentUserEntityRef
              ) {
                navigate(pathname.replace(/\/team-roles$/, ''));
                setTimeout(() => revokeAccess(), 0);
              }
            },
            onError: () => {
              onClose();
            },
          },
        );
      }}
    />
  );
};
